// src/components/AuthActivity/EventDetail/EventDetail.js

import React from "react";
import { X, MapPin, Clock, Globe, Cpu, User, AppWindow } from "lucide-react";
import {
  FaShieldAlt,
  FaExclamationTriangle,
  FaClipboardList,
  FaFingerprint,
} from "react-icons/fa";
import RiskBadge from "../RiskBadge";
import OutcomeBadge from "../OutcomeBadge";
import RiskFlag from "../RiskFlag";
import Loader from "../Loader";
import "./EventDetail.css";

const EventDetail = ({ event, loading, onClose }) => {
  if (loading) {
    return (
      <div className="event-detail">
        <div className="event-detail__header">
          <h3 className="event-detail__title">Event Details</h3>
          <button className="event-detail__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <Loader text="Loading event details..." />
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="event-detail">
      {/* Header */}
      <div className="event-detail__header">
        <h3 className="event-detail__title">
          <FaFingerprint size={16} style={{ marginRight: 8 }} />
          Event Details
        </h3>
        <button className="event-detail__close" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Identity */}
      <div className="event-detail__identity-section">
        <div className="event-detail__identity">{event.identity}</div>
        <div className="event-detail__badges">
          <RiskBadge level={event.riskLevel} />
          <OutcomeBadge outcome={event.outcome} />
        </div>
        <div className="event-detail__time">
          <Clock size={13} style={{ marginRight: 6 }} />
          {event.timeAgo}
        </div>
      </div>

      {/* Info Grid */}
      <div className="event-detail__info-grid">
        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <MapPin size={12} /> Location
          </span>
          <span className="event-detail__info-value">{event.location}</span>
        </div>

        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <Cpu size={12} /> Device
          </span>
          <span className="event-detail__info-value">{event.device}</span>
        </div>

        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <Globe size={12} /> IP Address
          </span>
          <span className="event-detail__info-value">{event.ipAddress}</span>
        </div>

        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <User size={12} /> Identity Type
          </span>
          <span className="event-detail__info-value event-detail__info-value--cap">
            {event.identityType}
          </span>
        </div>

        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <AppWindow size={12} /> Resource
          </span>
          <span className="event-detail__info-value">
            {event.resource || "N/A"}
          </span>
        </div>

        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <AppWindow size={12} /> Application
          </span>
          <span className="event-detail__info-value">
            {event.appDisplayName || "N/A"}
          </span>
        </div>

        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <Globe size={12} /> Client App
          </span>
          <span className="event-detail__info-value">
            {event.clientAppUsed || "N/A"}
          </span>
        </div>

        <div className="event-detail__info-item">
          <span className="event-detail__info-label">
            <FaShieldAlt size={12} /> Risk Score
          </span>
          <span className="event-detail__info-value">
            <span className={`event-detail__risk-score event-detail__risk-score--${event.riskLevel}`}>
              {event.riskScore}
            </span>
            <span className="event-detail__risk-max">/ 100</span>
          </span>
        </div>
      </div>

      {/* Risk Flags */}
      {event.riskFlags?.length > 0 && (
        <div className="event-detail__section">
          <h4 className="event-detail__section-title">
            <FaExclamationTriangle size={13} style={{ marginRight: 8 }} />
            Risk Flags
          </h4>
          <div className="event-detail__flags">
            {event.riskFlags.map((flag, idx) => (
              <RiskFlag key={idx} label={flag} />
            ))}
          </div>
        </div>
      )}

      {/* Detections */}
      {event.detections?.length > 0 && (
        <div className="event-detail__section">
          <h4 className="event-detail__section-title">
            <FaShieldAlt size={13} style={{ marginRight: 8 }} />
            Detections
          </h4>
          {event.detections.map((det, idx) => (
            <div key={idx} className="event-detail__detection">
              <div className="event-detail__detection-header">
                <span className="event-detail__detection-name">
                  {det.name}
                </span>
                <span className="event-detail__detection-id">
                  {det.useCaseId}
                </span>
              </div>
              <div className="event-detail__detection-category">
                {det.category}
              </div>

              {det.responseAction?.length > 0 && (
                <div className="event-detail__actions">
                  <span className="event-detail__actions-label">
                    <FaClipboardList size={12} style={{ marginRight: 6 }} />
                    Recommended Actions:
                  </span>
                  <ul className="event-detail__actions-list">
                    {det.responseAction.map((action, aIdx) => (
                      <li key={aIdx} className="event-detail__action-item">
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="event-detail__footer">
        <span className="event-detail__event-id">
          Event ID: {event.id}
        </span>
      </div>
    </div>
  );
};

export default EventDetail;